import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { faSync } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Theme,
  Typography,
  debounce,
  styled,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import {
  ClientAssignmentsViewModel,
  ClientInfoViewModel,
  ClientViewModel,
  InvoicePayerViewModel,
} from '@rsmus/ecp-financeservice'
import {
  CustomErrorAlert,
  CustomSuccessAlert,
} from 'src/components/forms/Alert'
import SectionHeader from '../../layouts/SectionHeader'
import { Styles } from '../../../types'
import { useAppSelector } from '../../../utils/hooks'
import { tokens } from '../../../styles/materialTheme'
import { formatCurrency } from '../../../rsmCoreComponents/utils/formatters'
import {
  isCemFeatureEnabled,
  isFeatureFlagEnabled,
} from '../../../rsmCoreComponents/utils/featureFlagUtils'
import {
  CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT,
  CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
  CEM_FEATURE_PAYMENT_PAY_INVOICES,
  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
} from '../../../utils/constants/constants'
import {
  getCemFeatures,
  getUserInfo,
} from '../../../store/userInfo/userInfoSlice'
import api from '../../../api'
import { InvoiceSelectedFiltersConstants } from '../../../store/invoices/invoiceSelectedFiltersSlice'
import {
  PayOnAccountClientData,
  setInvoicePayers,
  setIsAccountInfoLoaded,
  setPayOnAccountClient,
  setPayingInvoices,
  setSelectedInvoices,
  setselectedAccInvoices,
  resetAll as resetSelectedInvoicesSlice,
  setIsEditAutoPay,
} from '../../../store/invoices/invoiceSelectedInvoicesSlice'
import { transformOpenInvoiceData } from '../../../store/invoices/invoiceSearchService'
import AccountUserIcon from '../../icons/AccountUserIcon/AccountUserIcon'
import { ArrowDownIcon } from '../../icons'
import { resetAll as resetPaymentInfoSlice } from '../../../store/invoices/paymentInfoSlice'
import ConfirmCancelAutopayDialog from './ConfirmCancelAutopayDialog'
import AutopayDetailsSection from './AutopayDetailsSection'

const styles: Styles = {
  cardContainer: (theme) => ({
    background: theme.palette.common.white,
    border: `0.0625rem solid ${tokens.colors.rsmGray.disabled}`,
    padding: '1.5rem',
    fontFamily: 'prelo-book',
    fontWeight: 500,
  }),
  welcomeHeader: {
    fontWeight: 700,
    fontSize: '1.875rem',
    lineHeight: '2.5rem',
    fontStyle: 'italic',
  },
  clientName: (theme) => ({
    fontWeight: 700,
    fontSize: '1.5rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1.2rem',
    },
  }),
  clientNumber: {
    fontWeight: 700,
    color: tokens.colors.rsmBlue.primary,
  },
  cellTitle: (theme) => ({
    fontWeight: 400,
    fontSize: '1.25rem',
    lineHeight: '2.5rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1rem',
      lineHeight: '1.5rem',
    },
  }),
  amount: (theme) => ({
    fontWeight: 700,
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
    marginBottom: '.5rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1.375rem',
      lineHeight: '1.5rem',
    },
  }),
  otherPaymentOptionContainer: {
    paddingTop: '0rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start start',
  },
  payNowButton: (theme) => ({
    width: '12rem',
    marginTop: '0rem',
    fontSize: '1rem',
    [theme.breakpoints.only('mobile')]: {
      width: '100%',
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: '1.5rem',
    },
  }),
  payOnAccountButton: (theme) => ({
    width: '12rem',
    marginTop: '1rem',
    fontSize: '1rem',
    [theme.breakpoints.only('mobile')]: {
      width: '100%',
      marginTop: '0.5rem',
    },
  }),
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 50,
  },
  payNowContainer: (theme) => ({
    marginTop: '0.5rem',
    [theme.breakpoints.only('mobile')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  }),
  autoPayiconContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.05rem',
    height: '3rem',
    marginTop: '-0.95rem',
    marginBottom: '0.5rem',
    font: 'Prelo',
    fontStyle: 'italic',
  },
  autoPayEnabled: (theme) => ({
    color: theme.palette.success.main,
    gap: '0.1rem',
    fontWeight: 600,
  }),
  autoPay: (theme) => ({
    fontFamily: 'Prelo-Book',
    color: theme.palette.grey[800],
    fontWeight: 500,
    fontSize: '1.125rem',
  }),
  autoPayButton: (theme) => ({
    width: '12rem',
    marginTop: '1rem',
    fontSize: '1rem',
    [theme.breakpoints.only('mobile')]: {
      width: '100%',
      marginTop: '0.5rem',
    },
  }),
  setupButton: (theme) => ({
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
    fontWeight: 600,
  }),

  updateButton: (theme) => ({
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
    fontWeight: 600,
  }),
  cancelButton: (theme) => ({
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
    fontWeight: 500,
    paddingTop: 0,
    paddingBottom: 0,
    paddingRight: '1.875rem',
    paddingLeft: '0.625rem',
    marginRight: 'auto',
  }),
  check: (theme) => ({
    color: theme.palette.secondary.main,
  }),
}

type GridProps = {
  clientSelected: boolean
  layoutContainer: 'dashboard' | 'accountManagement'
}

const GridWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(12, [col] 1fr)',
  gridTemplateRows: '[row] auto [row] auto [row] auto [row] auto [row]',
  gridGap: '3rem',
  [theme.breakpoints.only('tablet')]: {
    gridGap: '2rem',
  },
  [theme.breakpoints.down('tablet')]: {
    gridGap: '1rem',
  },
}))

const WelcomeBox = styled('div')(({ theme }) => ({
  gridColumn: 'col 1 / span 2',
  gridRow: 'row 1 / span 1',
  [theme.breakpoints.only('desktop')]: {
    display: 'flex',
    justifyContent: 'center',
  },
  [theme.breakpoints.only('tablet')]: {
    gridColumn: 'col 3 / span 9',
    gridRow: 'row 1 / span 1',
  },
  [theme.breakpoints.down('tablet')]: {
    display: 'none',
  },
}))

const IconBox = styled('div')(({ theme }) => ({
  gridColumn: 'col 1 / span 2',
  gridRow: 'row 2 / span 2',
  [theme.breakpoints.only('desktop')]: {
    display: 'flex',
    justifyContent: 'center',
  },
  [theme.breakpoints.only('tablet')]: {
    gridColumn: 'col 1 / span 2',
    gridRow: 'row 1 / span 4',
  },
  [theme.breakpoints.down('tablet')]: {
    display: 'none',
  },
}))

const ClientSelectorBox = styled('div')<GridProps>(
  ({ theme, clientSelected, layoutContainer }) => ({
    gridColumn: clientSelected ? 'col 3 / span 5' : 'col 3 / span 7',
    gridRow: 'row 2 / span 1',
    [theme.breakpoints.only('tablet')]: {
      gridColumn:
        // eslint-disable-next-line no-nested-ternary
        layoutContainer === 'dashboard'
          ? 'col 3 / span 6'
          : clientSelected
          ? 'col 3 / span 6'
          : 'col 3 / span 9',
      gridRow: 'row 2 / span 1',
    },
    [theme.breakpoints.down('tablet')]: {
      gridColumn: 'col 1 / span 12',
      gridRow: 'row 1 / span 1',
    },
  }),
)

const ClientNumberBox = styled('div')(({ theme }) => ({
  gridColumn: 'col 8 / span 2',
  gridRow: 'row 2 / span 2',
  [theme.breakpoints.only('tablet')]: {
    gridColumn: 'col 3 / span 6',
    gridRow: 'row 3 / span 1',
  },
  [theme.breakpoints.down('tablet')]: {
    gridColumn: 'col 1 / span 6',
    gridRow: 'row 2 / span 2',
  },
}))

const ClientAddressBox = styled('div')(({ theme }) => ({
  whiteSpace: 'pre-line',
  gridColumn: 'col 3 / span 3',
  gridRow: 'row 3 / span 1',
  [theme.breakpoints.only('tablet')]: {
    gridColumn: 'col 3 / span 6',
    gridRow: 'row 4 / span 1',
  },
  [theme.breakpoints.down('tablet')]: {
    gridColumn: 'col 7 / span 6',
    gridRow: 'row 2 / span 1',
  },
}))

const ClientBusinessPhoneBox = styled('div')(({ theme }) => ({
  gridColumn: 'col 6 / span 2',
  gridRow: 'row 3 / span 1',
  [theme.breakpoints.only('tablet')]: {
    display: 'none',
  },
  [theme.breakpoints.down('tablet')]: {
    display: 'none',
  },
}))

const PayBox = styled('div')<GridProps>(
  ({ theme, clientSelected, layoutContainer }) => ({
    alignSelf: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start start',
    gridColumn: 'col 10 / span 3',
    gridRow: 'row 1 / span 4',
    borderLeft:
      clientSelected || layoutContainer === 'dashboard'
        ? `0.0156rem solid ${tokens.colors.rsmGray.dividers}`
        : 'none',
    [theme.breakpoints.only('desktop')]: {
      paddingLeft: '1rem',
      paddingRight: 0,
      justifySelf: 'center',
      width: '100%',
      minWidth: '18.75rem',
    },
    [theme.breakpoints.only('tablet')]: {
      gridColumn: 'col 9 / span 4',
      gridRow: 'row 2 / span 4',
      paddingLeft: '0.5rem',
      paddingRight: '0.5rem',
      justifySelf: 'center',
    },
    [theme.breakpoints.down('tablet')]: {
      alignItems: 'stretch',
      gridColumn: 'col 1 / span 12',
      gridRow: 'row 3 / span 1',
      borderLeft: 'none',
      paddingLeft: '0',
      paddingRight: '0',
      borderTop:
        clientSelected || layoutContainer === 'dashboard'
          ? `0.0156rem solid ${tokens.colors.rsmGray.dividers}`
          : 'none',
    },
  }),
)

const PayBoxContent = styled('div')(({ theme }) => ({
  maxWidth: '100%',
  [theme.breakpoints.only('mobile')]: {
    maxWidth: '100%',
  },
}))

const PayNowNoteBox = styled('div')(() => ({
  alignSelf: 'center',
  marginTop: '1rem',
  marginBottom: '1rem',
}))

const NoClientNoteBox = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  gridColumn: 'col 10 / span 3',
  gridRow: 'row 1 / span 4',
  [theme.breakpoints.only('tablet')]: {
    gridColumn: 'col 3 / span 9',
    gridRow: 'row 3 / span 1',
  },
  [theme.breakpoints.down('tablet')]: {
    gridColumn: 'col 1 / span 12',
    gridRow: 'row 3 / span 1',
  },
}))

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: 'underline',
}))

export interface AccountInformationProps {
  layoutContainer: 'dashboard' | 'accountManagement'
}

const AccountInformation = ({ layoutContainer }: AccountInformationProps) => {
  const { t } = useTranslation()
  const { clientId = '' } = useParams()
  const cemFeatures = useSelector(getCemFeatures)
  const userInfo = useAppSelector(getUserInfo)
  const clientAdmin = useMemo(
    () =>
      isCemFeatureEnabled(CEM_FEATURE_PAYMENT_CLIENT_ADMIN, cemFeatures, 'any'),
    [cemFeatures],
  )
  const isAccountManager = useMemo(
    () =>
      isCemFeatureEnabled(
        CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT,
        cemFeatures,
        'any',
      ),
    [cemFeatures],
  )

  const invoicingEnabled = useMemo(
    () =>
      isCemFeatureEnabled(
        [
          CEM_FEATURE_PAYMENT_PAY_INVOICES,
          CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
          CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
        ],
        cemFeatures,
        'any',
      ),
    [userInfo, cemFeatures],
  )

  const dispatch = useDispatch()
  const navigate = useNavigate()
  let abortController: AbortController | null = null
  const [totalOpenAmount, setTotalOpenAmount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const [value, setValue] = useState<ClientViewModel | null>(null)
  const [primaryAddress, setPrimaryAddress] = useState<string>('')
  const [businessPhone, setBusinessPhone] = useState<string>('')
  const [client, setClient] = useState<ClientInfoViewModel | null>(null)
  const [clients, setClients] = useState<ClientViewModel[]>([])
  const [totalClientAssignments, setTotalClientAssignments] = useState<
    number | undefined
  >(undefined)

  const [openSearch, setOpenSearch] = useState<boolean>(false)
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false)
  const [clientList, setClientList] = useState<ClientViewModel[]>([])
  const [openAutopayCancelDialog, setOpenAutopayCancelDialog] =
    useState<boolean>(false)
  const [isAutoPayExists, setAutoPayExists] = useState<boolean>(false)
  const [openAutopayCancelSuccess, setOpenAutopayCancelSuccess] =
    useState<boolean>(false)
  const [openAutopayCancelError, setOpenAutopayCancelError] =
    useState<boolean>(false)

  const fetchAutoPayExist = async () => {
    try {
      if (!clientId) {
        setAutoPayExists(false)
        return
      }
      const autoPayExists = await api.finance.payment_GetClientAutoPayExist(
        Number(clientId),
      )

      if (autoPayExists?.data) {
        setAutoPayExists(true)
      } else {
        setAutoPayExists(false)
      }
    } catch (error) {
      console.log(error)
      setAutoPayExists(false)
    }
  }
  const fetchTotalOpenAmount = useCallback(async () => {
    try {
      setIsLoading(true)
      let openAmont = null

      if (!clientId && clientAdmin) {
        openAmont = await api.finance.invoice_GetTotalOpenAmount(
          InvoiceSelectedFiltersConstants.DefaultCurrency,
        )
      }
      if (clientId) {
        openAmont = await api.finance.invoice_GetClientTotalOpenAmount(
          InvoiceSelectedFiltersConstants.DefaultCurrency,
          clientId,
        )
      }

      if (openAmont?.data) {
        setTotalOpenAmount(openAmont.data)
      } else {
        setTotalOpenAmount(0)
      }
      setIsLoading(false)
      fetchAutoPayExist()
    } catch (error) {
      setTotalOpenAmount(0)
      setIsLoading(false)
    }
  }, [clientId, clientAdmin, setTotalOpenAmount, setIsLoading])

  const clientChange = (newClientId: string) => {
    if (newClientId) {
      if (isAccountManager) {
        setOpenSearch(false)
      }
      if (layoutContainer === 'accountManagement') {
        navigate(`/invoicing/account-management/${newClientId}`)
        return
      }
      navigate(`/invoicing/dashboard/${newClientId}`)
    }
  }

  const fetchClientInfo = useCallback(async () => {
    try {
      let clientInfo: ClientInfoViewModel | undefined
      if (clientId) {
        clientInfo = (await api.finance.client_GetClientInfo(+clientId))?.data
      }

      if (clientInfo) {
        setClient(clientInfo)
        setValue({
          clientId,
          name: clientInfo.clientName,
          masterClientId: '',
        })
        const address = `${
          clientInfo.addressLine1 ? `${clientInfo.addressLine1}\n` : ''
        }${clientInfo.addressLine2 ? `${clientInfo.addressLine2}\n` : ''}${
          clientInfo.city
        }, ${clientInfo.stateProvince} ${clientInfo.country} ${
          clientInfo.postalCode
        }`
        setPrimaryAddress(address)
        setBusinessPhone(clientInfo.phoneNumber || '')
      } else {
        setPrimaryAddress('')
        setBusinessPhone('')
        setValue(null)
      }
      setIsLoading(false)
    } catch (error) {
      setPrimaryAddress('')
      setBusinessPhone('')
      setValue(null)
    }
  }, [
    api.finance.client_GetClientInfo,
    clientId,
    setClient,
    setPrimaryAddress,
    setBusinessPhone,
    setIsLoading,
  ])

  const fetchClients = useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: ClientAssignmentsViewModel) => void,
        ) => {
          if (!request.input && isAccountManager) {
            callback({ totalClientAssignments: undefined, clients: [] })
            return
          }
          // Cancel the previous request before making a new request
          if (abortController) {
            abortController.abort('New request initiated')
          }
          abortController = new AbortController()
          const abortSignal = abortController.signal
          api.finance
            .client_GetClients(request.input, abortSignal)
            .then((response) => {
              callback(response?.data)
            })
        },
        500,
      ),
    [],
  )

  useEffect(() => {
    setIsSearchLoading(true)
    fetchClients({ input: inputValue }, (results) => {
      setClients([...(results?.clients || [])])
      setTotalClientAssignments(results?.totalClientAssignments)
      setIsSearchLoading(false)
    })
  }, [inputValue])

  useEffect(() => {
    fetchTotalOpenAmount()
    fetchClientInfo()
    if (isAccountManager && clientId.length > 0) {
      setOpenSearch(false)
    }
  }, [clientId])

  useEffect(() => {
    const allAccounts: ClientViewModel = {
      clientId: '0',
      name: 'All accounts',
      masterClientId: '',
    }
    const newClientList =
      layoutContainer === 'dashboard' && clientId
        ? [allAccounts, ...clients]
        : clients
    setClientList(newClientList)
  }, [clients, clientId, layoutContainer])

  const handlePayNow = async (e: SyntheticEvent) => {
    e.preventDefault()
    dispatch(resetSelectedInvoicesSlice())
    dispatch(resetPaymentInfoSlice())
    try {
      if (totalOpenAmount === 0) return
      setIsLoading(true)
      const invoices =
        !clientId && clientAdmin
          ? await api.finance.invoice_GetOpenInvoices(
              InvoiceSelectedFiltersConstants.DefaultCurrency,
            )
          : await api.finance.invoice_GetOpenClientInvoices(
              InvoiceSelectedFiltersConstants.DefaultCurrency,
              clientId,
            )

      if (invoices.data) {
        const invoiceData = transformOpenInvoiceData(invoices)
        dispatch(setSelectedInvoices(invoiceData))
        dispatch(setPayingInvoices(invoiceData))
        dispatch(setselectedAccInvoices(invoiceData))
        dispatch(setIsAccountInfoLoaded(true))
        navigate('/invoicing/invoices/pay-invoices')
      } else {
        dispatch(setSelectedInvoices([]))
        dispatch(setPayingInvoices([]))
      }
      setIsLoading(false)
    } catch (error) {
      dispatch(setSelectedInvoices([]))
      dispatch(setPayingInvoices([]))
      setIsLoading(false)
    }
  }

  const handlePayOnAccountAutoPay = async (
    e: SyntheticEvent,
    isPayOnAccount: boolean,
  ) => {
    e.preventDefault()
    try {
      setIsLoading(true)

      if (client) {
        const invoicePayers: InvoicePayerViewModel[] = []

        if (isAccountManager) {
          const invoicePayer: InvoicePayerViewModel = {
            name: client?.clientName,
            payerId: clientId,
            masterClientId: undefined,
          }
          invoicePayers.push(invoicePayer)
        }

        if (clientAdmin) {
          clients.forEach((clientItem: ClientViewModel) => {
            const invoicePayer: InvoicePayerViewModel = {
              name: clientItem?.name,
              payerId: clientItem?.clientId,
              masterClientId: clientItem?.masterClientId,
            }
            invoicePayers.push(invoicePayer)
          })
        }

        dispatch(setInvoicePayers(invoicePayers))
        const payOnAccountAutoPayClient: PayOnAccountClientData = {
          name: client?.clientName,
          clientId,
          isCreditCardEnabled: client?.creditCardEnabled,
          totalOpenAmount,
        }
        dispatch(setPayOnAccountClient(payOnAccountAutoPayClient))
        if (isPayOnAccount) {
          navigate('/invoicing/invoices/pay-on-account')
        } else {
          dispatch(setIsEditAutoPay(isAutoPayExists))
          navigate('/invoicing/invoices/autopay')
        }
      }

      setIsLoading(false)
    } catch (error) {
      dispatch(setPayOnAccountClient(null))
      dispatch(setInvoicePayers([]))
      setIsLoading(false)
    }
  }

  const handleOpenSearch = () => {
    if (isAccountManager && inputValue.length > 0) {
      setOpenSearch(true)
    } else if (!isAccountManager) {
      setOpenSearch(true)
    }
  }

  const handleCancelAutoPayResult = (success: boolean) => {
    if (success) {
      setAutoPayExists(false)
      setOpenAutopayCancelSuccess(true)
    } else {
      console.log(success)
      setOpenAutopayCancelError(true)
    }
  }

  const computedStyles = {
    openAmountContainer: useCallback(
      (theme: Theme) =>
        isAutoPayExists
          ? {
              [theme.breakpoints.only('mobile')]: {
                flex: '1 1 50%',
                marginTop: '1rem',
              },
            }
          : {
              [theme.breakpoints.only('mobile')]: {
                display: 'inline-block',
              },
            },
      [isAutoPayExists],
    ),
    openAmountPayContainer: useCallback(
      (theme: Theme) =>
        isAutoPayExists
          ? {
              display: 'inline-block',
              [theme.breakpoints.only('desktop')]: {
                width: '100%',
              },
              [theme.breakpoints.only('mobile')]: {
                display: 'flex',
                width: '100%',
              },
            }
          : {
              display: 'inline-block',
              width: '100%',
            },
      [isAutoPayExists],
    ),
  }

  return (
    <>
      <Box sx={styles.cardContainer}>
        <SectionHeader
          title={t('Invoicing.AccountInformation')}
          testId="Lbl_AccountInformation_Header"
        />
        {isLoading ? (
          <Box sx={styles.loading}>
            <CircularProgress
              size="3rem"
              color="secondary"
              data-testid="Spn_AccountInformation_Loading"
            />
          </Box>
        ) : (
          <GridWrapper>
            <WelcomeBox>
              <Box sx={styles.welcomeHeader} data-testid="Txt_Welcome_Header">
                {t('Invoicing.AccountInformationCard.Welcome')}
              </Box>
            </WelcomeBox>
            <IconBox>
              <AccountUserIcon />
            </IconBox>
            <ClientSelectorBox
              clientSelected={!!clientId}
              layoutContainer={layoutContainer}>
              {layoutContainer === 'dashboard' &&
              clientId &&
              totalClientAssignments &&
              totalClientAssignments === 1 ? (
                <Box component="span" sx={styles.clientName}>
                  {clients?.[0]?.name}
                </Box>
              ) : (
                // If a clientId exists, wait until the client is loaded so the defaultValue shows the correct value (because it does not rerender).
                // Also, only render after 'clients' is loaded we don't flash the autocomplete when the user only has one client.
                (layoutContainer !== 'dashboard' ||
                  (totalClientAssignments && totalClientAssignments > 1)) &&
                (!clientId || client) && (
                  <Autocomplete
                    popupIcon={<ArrowDownIcon />}
                    id="payer"
                    fullWidth
                    onOpen={handleOpenSearch}
                    onFocus={() => {
                      setInputValue('')
                      setValue(null)
                    }}
                    onBlur={() => setOpenSearch(false)}
                    open={openSearch}
                    loading={isSearchLoading}
                    defaultValue={undefined}
                    value={value}
                    onChange={(
                      _: SyntheticEvent,
                      newValue: ClientViewModel | null,
                      reason: string,
                    ) => {
                      if (
                        newValue?.clientId === '0' ||
                        (clientId && reason === 'clear')
                      ) {
                        if (layoutContainer === 'dashboard') {
                          navigate('/invoicing/dashboard')
                        } else {
                          navigate('/invoicing/account-management')
                        }
                        return
                      }
                      setValue(newValue)
                      clientChange(newValue?.clientId || '')
                    }}
                    options={clientList}
                    getOptionLabel={(c) => {
                      if (c.clientId === '0') {
                        return c.name || ''
                      }
                      return c ? `${c.name} ( ${c.clientId} )` : ''
                    }}
                    isOptionEqualToValue={(option, val) =>
                      option?.clientId === val?.clientId
                    }
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === 'input') {
                        setInputValue(newInputValue)
                        if (isAccountManager) {
                          if (newInputValue.length > 0) {
                            setOpenSearch(true)
                          } else {
                            setOpenSearch(false)
                          }
                        }
                      }
                    }}
                    noOptionsText={t(
                      'Invoicing.AccountInformationCard.NoResults',
                    )}
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        placeholder={
                          isAccountManager
                            ? t(
                                'Invoicing.AccountInformationCard.ClientAutocompleteInternalPlaceholder',
                              )
                            : t(
                                'Invoicing.AccountInformationCard.ClientAutocompleteExternalPlaceholder',
                              )
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isSearchLoading && inputValue && (
                                <CircularProgress size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )
              )}
            </ClientSelectorBox>
            {clientId && (
              <ClientNumberBox>
                <>
                  <Box
                    sx={styles.clientNumber}
                    data-testid="Lbl_AccountManagement_Client_Number">
                    {t('Invoicing.AccountInformationCard.ClientNumber')}
                  </Box>
                  {clientId}
                </>
              </ClientNumberBox>
            )}
            {clientId && (
              <ClientAddressBox>
                <>
                  <Box>
                    <b>
                      {t(
                        'Invoicing.AccountInformationCard.PrimaryAccountAddress',
                      )}
                    </b>
                  </Box>
                  {primaryAddress}
                </>
              </ClientAddressBox>
            )}
            {clientId && (
              <ClientBusinessPhoneBox>
                <>
                  <Box data-testid="Txt_AccountManagement_Business_Phone">
                    <b>{t('Invoicing.AccountInformationCard.BusinessPhone')}</b>
                  </Box>
                  {businessPhone}
                </>
              </ClientBusinessPhoneBox>
            )}
            <PayBox
              clientSelected={!!clientId}
              layoutContainer={layoutContainer}>
              <PayBoxContent>
                {((clientId && isAccountManager) || clientAdmin) && (
                  <>
                    <Box sx={computedStyles.openAmountPayContainer}>
                      <Box
                        sx={computedStyles.openAmountContainer}
                        component="h3">
                        <Box
                          sx={styles.cellTitle}
                          data-testid="Txt_TotalOpenAmount_Title">
                          {t('Invoicing.TotalOpenAmount')}:
                        </Box>
                        <Box
                          sx={styles.amount}
                          data-testid="Txt_TotalOpenAmount_Amount">
                          {totalOpenAmount > 0
                            ? formatCurrency(
                                totalOpenAmount,
                                InvoiceSelectedFiltersConstants.DefaultCurrency,
                              )
                            : formatCurrency(
                                totalOpenAmount,
                                InvoiceSelectedFiltersConstants.DefaultCurrency,
                              ).split('.')[0]}{' '}
                          {InvoiceSelectedFiltersConstants.DefaultCurrency.toUpperCase()}
                        </Box>
                      </Box>
                      {isFeatureFlagEnabled(
                        'Invoicing_Autopay',
                        userInfo.emailAddress,
                        cemFeatures,
                      ) &&
                        isAutoPayExists &&
                        invoicingEnabled && (
                          <AutopayDetailsSection
                            client={client}
                            handlePayOnAccountAutoPay={
                              handlePayOnAccountAutoPay
                            }
                            setOpenAutopayCancelDialog={
                              setOpenAutopayCancelDialog
                            }
                          />
                        )}
                      {isFeatureFlagEnabled(
                        'Invoicing_Autopay',
                        userInfo.emailAddress,
                        cemFeatures,
                      ) &&
                        !isAutoPayExists &&
                        invoicingEnabled &&
                        client && (
                          // Autopay OFF section
                          <Box sx={styles.autoPayiconContainer}>
                            <FontAwesomeIcon
                              icon={faSync}
                              color={tokens.colors.rsmGreen.primary}
                              aria-hidden="true"
                            />
                            <Typography
                              sx={styles.autoPay}
                              data-testid="AutopayOffText">
                              {t('Invoicing.AutopayOff')}
                            </Typography>
                            <Button
                              data-testid="Btn_AccountInformation_SetupAutopay"
                              aria-label="Set up autopay"
                              sx={styles.setupButton}
                              disabled={!client}
                              disableFocusRipple
                              disableRipple
                              onClick={(e) =>
                                handlePayOnAccountAutoPay(e, false)
                              }>
                              {t('Invoicing.Setup')}
                            </Button>
                          </Box>
                        )}
                    </Box>
                    <Box sx={styles.payNowContainer}>
                      <Button
                        disabled={totalOpenAmount === 0}
                        disableFocusRipple
                        disableRipple
                        sx={styles.payNowButton}
                        variant="contained"
                        data-testid="Btn_AccountInformation_PayNow"
                        onClick={handlePayNow}>
                        {t('Invoicing.PayNow')}
                      </Button>
                    </Box>
                  </>
                )}
                {clientId && (
                  <Box sx={styles.otherPaymentOptionContainer}>
                    <Button
                      disabled={!client}
                      disableFocusRipple
                      disableRipple
                      sx={styles.payOnAccountButton}
                      variant="contained"
                      data-testid="Btn_AccountInformation_PayOnAccount"
                      onClick={(e) => handlePayOnAccountAutoPay(e, true)}>
                      {t('Invoicing.PayOnAccount')}
                    </Button>
                  </Box>
                )}
                {!clientId && clientAdmin && (
                  <PayNowNoteBox>
                    <Trans
                      i18nKey="Invoicing.AccountInformationCard.PayNowNote"
                      components={{ b: <b /> }}
                    />
                  </PayNowNoteBox>
                )}
              </PayBoxContent>
            </PayBox>
            {!clientId && isAccountManager && (
              <NoClientNoteBox>
                <i>
                  <Trans
                    i18nKey="Invoicing.AccountInformationCard.NoClientNote"
                    components={{ b: <b /> }}
                  />
                </i>
              </NoClientNoteBox>
            )}
          </GridWrapper>
        )}
      </Box>
      <ConfirmCancelAutopayDialog
        open={openAutopayCancelDialog}
        onClose={() => setOpenAutopayCancelDialog(false)}
        handleCancelAutoPayResult={handleCancelAutoPayResult}
        clientId={clientId}
      />
      <CustomErrorAlert
        testId="Banner_AccountInformation_AutopayCancelError"
        header="Invoicing.AccountInformationCard.AutopayCancelErrorHeader"
        messageElement={
          <Trans
            i18nKey="Invoicing.AccountInformationCard.AutopayCancelErrorMessage"
            defaults={t(
              'Invoicing.AccountInformationCard.AutopayCancelErrorMessage',
            )}
            components={[
              <StyledLink
                sx={(theme) => ({
                  color: theme.palette.secondary.main,
                  textDecoration: 'underline',
                })}
                href="/contact-us/support-page"
                target="_self"
                rel="noopener noreferrer"
              />,
            ]}
            values={{
              supportNumber: userInfo.isEmployee
                ? '877.462.7573'
                : '833-518-3394',
            }}
          />
        }
        open={openAutopayCancelError}
        close={() => setOpenAutopayCancelError(false)}
        disableAutoHide
      />
      <CustomSuccessAlert
        testId="Banner_AccountInformation_AutopayCancelSuccess"
        header="Invoicing.AccountInformationCard.AutopayCancelSuccessHeader"
        message="Invoicing.AccountInformationCard.AutopayCancelSuccessMessage"
        open={openAutopayCancelSuccess}
        close={() => setOpenAutopayCancelSuccess(false)}
        disableAutoHide
      />
    </>
  )
}
export default AccountInformation
